import { CatalogueQuestion } from "../types/catalogue"

/** The first number in `label`: "5 - Very satisfied" gives 5, "Don't know" null. */
export const scaleValueOf = (label: string): number | null => {
  const m = /-?\d+(\.\d+)?/.exec(label)
  return m ? Number(m[0]) : null
}

const scored = (q: CatalogueQuestion) => q.options.filter((o) => !o.exception)

/** 1 to n by position, first option lowest; `reversed` makes the first option highest. */
export const positionScores = (
  q: CatalogueQuestion,
  reversed = false
): Record<string, number> => {
  const opts = scored(q)
  return Object.fromEntries(
    opts.map((o, i) => [o.label, reversed ? opts.length - i : i + 1])
  )
}

/** Whole numbers that follow each other with no gap: 1 to 5, or 10 down to 0. Age bands are not. */
const isRun = (values: number[]): boolean => {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted.every((v, i) => Number.isInteger(v) && (i === 0 || v === sorted[i - 1] + 1))
}

/**
 * The score of every option that is not an exception, or null when no score
 * can be worked out. Order of preference: the option codes of an ordered
 * (range) question, the number in each label when the numbers are a run of
 * whole numbers, the position in an ordered list.
 */
export const defaultScores = (q: CatalogueQuestion): Record<string, number> | null => {
  const opts = scored(q)
  if (opts.length < 2) return null
  if (q.ordinal && opts.every((o) => o.code !== undefined))
    return Object.fromEntries(opts.map((o) => [o.label, o.code as number]))
  const fromLabels = opts.map((o) => scaleValueOf(o.label))
  if (fromLabels.every((v): v is number => v !== null) && isRun(fromLabels))
    return Object.fromEntries(opts.map((o, i) => [o.label, fromLabels[i] as number]))
  return q.ordinal ? positionScores(q) : null
}

const SELECT_ONE = ["select-one", "range", "grid-one", "grid-range"]

export const isScale = (q: CatalogueQuestion): boolean => {
  if (!SELECT_ONE.includes(q.type)) return false
  if (q.type === "range" || q.type === "grid-range" || q.ordinal) return true
  return scored(q).length >= 3 && defaultScores(q) !== null
}

/** True when the scores are exactly 0 to 10. */
export const isNps = (scores: Record<string, number>): boolean => {
  const values = Object.values(scores).sort((a, b) => a - b)
  return values.length === 11 && values.every((v, i) => v === i)
}

/** The labels holding the `size` highest (or lowest) scores, in questionnaire order; none for size 0. */
export const boxMembers = (
  q: CatalogueQuestion,
  scores: Record<string, number>,
  size: number,
  end: "top" | "bottom"
): string[] => {
  const labels = scored(q)
    .map((o) => o.label)
    .filter((l) => l in scores)
  const ranked = [...labels].sort((a, b) =>
    end === "top" ? scores[b] - scores[a] : scores[a] - scores[b]
  )
  const chosen = new Set(ranked.slice(0, Math.max(0, size)))
  return labels.filter((l) => chosen.has(l))
}

/**
 * The scored options that are neither in the top box of size `top` nor in
 * the bottom box of size `bottom` (0: no box), in questionnaire order; the
 * exceptions are never in it.
 */
export const middleMembers = (
  q: CatalogueQuestion,
  scores: Record<string, number>,
  top: number,
  bottom: number
): string[] => {
  const boxed = new Set([...boxMembers(q, scores, top, "top"), ...boxMembers(q, scores, bottom, "bottom")])
  return scored(q)
    .map((o) => o.label)
    .filter((l) => l in scores && !boxed.has(l))
}

/** Labels whose score lies in `[from, to]`, in questionnaire order. */
export const labelsInRange = (
  q: CatalogueQuestion,
  scores: Record<string, number>,
  from: number,
  to: number
): string[] =>
  scored(q)
    .map((o) => o.label)
    .filter((l) => l in scores && scores[l] >= from && scores[l] <= to)
